import { InvoiceTypeWithVehicleSales, OnboardingStepKey } from '@cbgms/api'
import { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { urlSignUpFinished } from '../constants/routes'
import { useAcceptTermsAndConditionsStep } from './AcceptTermsAndConditions/use-accept-terms-and-conditions-step'
import { useAddMechanicStep } from './AddMechanicStep/use-add-mechanic-step'
import { useCaptureGMSInformationStep } from './CaptureGMSInformationStep/use-capture-gms-information-step'
import { useContactInformationStep } from './ContactInformationStep/use-contact-information-step'
import { useAcceptHaynesProEULAStep } from './HaynesProEULA/use-haynes-pro-eula-step'
import {
  InvoiceNumbersStep,
  InvoiceNumbersVehiclePurchaseStep,
  InvoiceNumbersVehicleSalesStep,
  useInvoiceNumbersStep
} from './InvoiceNumbersStep/use-invoice-numbers-step'
import { useLabourRateStep } from './LabourRateStep/use-labour-rate-step'
import { useOnboardingCheckVATStep } from './OnboardingCheckVATStep/use-onboarding-check-vat-step'
import { useOnboardingWholesalerCredentialsStep } from './OnboardingWholesalerCredentialsStep/use-onboarding-wholesaler-credentials-step'
import { useOpeningHoursStep } from './OpeningHoursStep/use-opening-hours-step'
import { usePaymentMethodsStep } from './PaymentMethodsStep/use-payment-methods-step'
import { useSetupWholesalerStep } from './SetupWholesalersStep/use-setup-wholesalers-step'
import { useSignUpCredentialsStep } from './SignupCredentialsStep/use-signup-credentials-step'
import { IStep, NormalStep, OnboardingMode, useStepsProps, useStepsResult } from './types.d'
import { useTyreChangeStep } from './TyreChangeStep/use-tyre-change-step'
import { usePersistDecorator } from './use-persist-decorator'
import { useWelcomeMessageStep } from './WelcomeMessageStep/use-welcome-message-step'
import { InvoiceNumbersCTA } from './InvoiceNumbersStep/InvoiceNumbersCTA'
import { InvoiceNumbersVehicleSalesCTA } from './InvoiceNumbersStep/InvoiceNumbersVehicleSalesCTA'
import { InvoiceNumbersVehiclePurchaseCTA } from './InvoiceNumbersStep/InvoiceNumbersVehiclePurchaseCTA'
import { useRevenueGroupDefaultStep } from './RevenueGroupDefaultsStep/use-revenue-group-default-step'

export const useSteps = ({
  basePath,
  themeUUID,
  clearOnboardingToken,
  persistMode,
  onboardingInfo,
  ticketUUID
}: useStepsProps): useStepsResult => {
  const country = onboardingInfo?.country
  const onboardingMode = onboardingInfo.onboardingMode
  const history = useHistory()

  // build all steps.
  const wholesalerCredentialsStep = useOnboardingWholesalerCredentialsStep({
    basePath,
    onboardingInfo
  })
  const welcomeMessageStep = useWelcomeMessageStep({ basePath, onboardingInfo })

  const onboardingCheckVATStep = useOnboardingCheckVATStep({ basePath, country, onboardingInfo })
  const contactInformationStep = useContactInformationStep({
    basePath,
    validatedVATNumber: onboardingCheckVATStep.validatedVATNumber,
    wholesalerCredentials: wholesalerCredentialsStep.wholesalerCredentials,
    themeUUID,
    onboardingInfo
  })
  const signupCredentialsStep = useSignUpCredentialsStep({
    basePath,
    shopContactInformation: contactInformationStep.shopContactInformation,
    onboardingInfo
  })

  const labourRateStep = useLabourRateStep({ basePath, onboardingInfo })

  const prefilledEmployee = signupCredentialsStep.userCredentials
    ? {
        ...signupCredentialsStep.userCredentials,
        EmailAddressWork: signupCredentialsStep.userCredentials.EmailAddress,
        ProfileColour: '#cccccc'
      }
    : undefined
  const addMechanicStep = useAddMechanicStep({ basePath, prefilledEmployee, onboardingInfo })

  const openingsHoursStep = useOpeningHoursStep({ basePath, onboardingInfo })

  const invoiceSequenceStep = useInvoiceNumbersStep({
    basePath,
    onboardingInfo,
    CTAComponent: InvoiceNumbersCTA,
    Component: InvoiceNumbersStep,
    invoiceType: InvoiceTypeWithVehicleSales.WorkorderCustomer,
    stepName: OnboardingStepKey.InvoiceNumbers
  })

  const invoiceSequenceVehiclePurchaseStep = useInvoiceNumbersStep({
    basePath,
    onboardingInfo,
    CTAComponent: InvoiceNumbersVehiclePurchaseCTA,
    Component: InvoiceNumbersVehiclePurchaseStep,
    invoiceType: InvoiceTypeWithVehicleSales.VehiclePurchase,
    stepName: OnboardingStepKey.InvoiceSequenceVehiclePurchase
  })

  const invoiceSequenceVehicleSalesStep = useInvoiceNumbersStep({
    basePath,
    onboardingInfo,
    CTAComponent: InvoiceNumbersVehicleSalesCTA,
    Component: InvoiceNumbersVehicleSalesStep,
    invoiceType: InvoiceTypeWithVehicleSales.VehicleSale,
    stepName: OnboardingStepKey.InvoiceSequenceVehicleSales
  })

  const setupWholesalerStep = useSetupWholesalerStep({
    basePath,
    wholesalerCredentials: wholesalerCredentialsStep.wholesalerCredentials,
    onboardingInfo,
    ticketUUID: ticketUUID ?? null
  })

  const revenueGroupStep = useRevenueGroupDefaultStep({
    basePath,
    onboardingInfo
  })

  const acceptEULAStep = useAcceptTermsAndConditionsStep({
    basePath,
    onboardingInfo,
    country: country || contactInformationStep.shopContactInformation?.VisitorCountry || 'GB'
  })
  const acceptHaynesEULAStep = useAcceptHaynesProEULAStep({ basePath, onboardingInfo })

  const captureGMSInformationStep = useCaptureGMSInformationStep({ basePath, onboardingInfo })
  const tyreChangeStep = useTyreChangeStep({ basePath, onboardingInfo })
  const paymentMethodsStep = usePaymentMethodsStep({ basePath, onboardingInfo })
  const allSteps = [
    wholesalerCredentialsStep,
    signupCredentialsStep,
    welcomeMessageStep,
    onboardingCheckVATStep,
    contactInformationStep,
    openingsHoursStep,
    revenueGroupStep,
    labourRateStep,
    setupWholesalerStep,
    addMechanicStep,
    captureGMSInformationStep,
    invoiceSequenceStep,
    invoiceSequenceVehiclePurchaseStep,
    invoiceSequenceVehicleSalesStep,
    paymentMethodsStep,
    tyreChangeStep,
    acceptEULAStep,
    acceptHaynesEULAStep
  ]
  const stepsToExecute = useMemo(() => allSteps.filter(s => s.shouldExecuteStep), allSteps)

  const onFinish = useCallback(
    (failedSteps: OnboardingStepKey[] = []) => {
      let navigationUrl = '' // by default, send user to the dashboard
      if (onboardingMode === OnboardingMode.OnboardingViaWholesaler) {
        clearOnboardingToken()
        navigationUrl = urlSignUpFinished(basePath) // send users to signup finished
      }

      const isOnboardingFinished = !failedSteps.includes(OnboardingStepKey.SignUpCredentials)
      if (isOnboardingFinished) {
        history.push(navigationUrl)
      }
    },
    [clearOnboardingToken, onboardingMode]
  )

  const { steps, isPersisting } = usePersistDecorator(stepsToExecute as NormalStep<any>[], persistMode, onFinish)

  return {
    steps: steps as NormalStep<unknown>[],
    allSteps: allSteps as IStep<unknown>[],
    isPersisting
  }
}
